// src/utils/tactic/visibility.js
// ─────────────────────────────────────────────────────────────────────────
// Raycast line-of-sight, in LOGICAL map space.
//
// For each observer we sweep a fan of rays: three at every nearby wall corner
// (corner ± a hair, so the fan snaps exactly onto silhouette edges) plus a ring
// of evenly spaced rays that round out the sight radius where nothing blocks.
// Each ray stops at the closest wall it hits, and the stop points, sorted by
// angle, are the visibility polygon.
//
// Brush is deliberately NOT a wall — you can see *past* grass, just not *into*
// it. It is resolved separately in `computeTeamVision`, which reveals a brush
// only when that team has someone standing inside it.
// ─────────────────────────────────────────────────────────────────────────

import {
  findContainingPolygon,
  pointInPolygon,
} from "./mapTerrain";

const TAU = Math.PI * 2;
const EPS = 1e-9;
const CORNER_NUDGE = 1e-4;

const normalizeAngle = (a) => ((a % TAU) + TAU) % TAU;

/** Distance along the ray to a segment, or Infinity when it misses. */
function rayHit(ox, oy, dx, dy, ax, ay, bx, by) {
  const sx = bx - ax;
  const sy = by - ay;
  const denom = dx * sy - dy * sx;
  if (Math.abs(denom) < EPS) return Infinity; // parallel

  const wx = ax - ox;
  const wy = ay - oy;
  const t = (wx * sy - wy * sx) / denom; // along ray
  const u = (wx * dy - wy * dx) / denom; // along segment
  if (t < 0 || u < 0 || u > 1) return Infinity;
  return t;
}

/** Squared distance from a point to a segment — used to cull far walls. */
function pointSegmentDist2(px, py, ax, ay, bx, by) {
  const vx = bx - ax;
  const vy = by - ay;
  const len2 = vx * vx + vy * vy;
  let t = len2 ? ((px - ax) * vx + (py - ay) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const dx = ax + t * vx - px;
  const dy = ay + t * vy - py;
  return dx * dx + dy * dy;
}

/**
 * Visibility polygon for one observer.
 *
 * @param {[number, number]} origin  observer position, logical coords
 * @param {Array} segments           blocking segments [ax, ay, bx, by]
 * @param {number} radius            sight radius in logical units
 * @param {number} ringRays          base rays used to round the radius
 * @returns {Array<[number, number]>} polygon, already sorted by angle
 */
export function computeVisibilityPolygon(origin, segments, radius, ringRays = 72) {
  const [ox, oy] = origin;
  if (!Number.isFinite(ox) || !Number.isFinite(oy)) return [];

  // Only walls the sight circle actually touches can matter.
  const near = [];
  for (const s of segments) {
    if (pointSegmentDist2(ox, oy, s[0], s[1], s[2], s[3]) <= radius * radius) {
      near.push(s);
    }
  }

  const angles = [];
  for (let i = 0; i < ringRays; i++) angles.push((i / ringRays) * TAU);
  for (const s of near) {
    for (let k = 0; k < 2; k++) {
      const px = s[k * 2];
      const py = s[k * 2 + 1];
      const a = normalizeAngle(Math.atan2(py - oy, px - ox));
      angles.push(normalizeAngle(a - CORNER_NUDGE), a, normalizeAngle(a + CORNER_NUDGE));
    }
  }
  angles.sort((a, b) => a - b);

  const points = [];
  let prev = -1;
  for (const a of angles) {
    if (a - prev < 1e-9) continue; // collapse duplicate rays
    prev = a;

    const dx = Math.cos(a);
    const dy = Math.sin(a);
    let best = radius;
    for (const s of near) {
      const t = rayHit(ox, oy, dx, dy, s[0], s[1], s[2], s[3]);
      if (t < best) best = t;
    }
    points.push([ox + dx * best, oy + dy * best]);
  }
  return points;
}

/**
 * Resolve what one team can see this frame.
 *
 * Returns the per-observer sight polygons plus the brushes that stay dark.
 * The renderer paints the polygons as holes in the fog, then paints the hidden
 * brushes back over them — which is exactly the in-game rule that grass blocks
 * sight for everyone who is not standing in it.
 *
 * @returns {{ polygons: Array, hiddenBrush: Array, revealedBrushIndexes: Set }}
 */
export function computeTeamVision({
  observers = [],
  segments = [],
  brush = [],
  ringRays = 72,
} = {}) {
  const polygons = [];
  const revealedBrushIndexes = new Set();

  for (const obs of observers) {
    const { x, y, radius } = obs;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !(radius > 0)) continue;

    // Standing in grass reveals that whole patch to the team.
    const inside = findContainingPolygon(x, y, brush);
    if (inside >= 0) revealedBrushIndexes.add(inside);

    polygons.push({
      id: obs.id,
      radius,
      points: computeVisibilityPolygon([x, y], segments, radius, ringRays),
    });
  }

  const hiddenBrush = brush.filter((_, i) => !revealedBrushIndexes.has(i));
  return { polygons, hiddenBrush, revealedBrushIndexes };
}

/**
 * Is a point visible to a team? Used to dim enemy tokens the team cannot see.
 * A token inside an unrevealed brush is hidden even when the fan covers it.
 */
export function isPointVisible(x, y, vision, brush = []) {
  if (!vision) return true;

  const brushIndex = findContainingPolygon(x, y, brush);
  if (brushIndex >= 0 && !vision.revealedBrushIndexes.has(brushIndex)) return false;

  return vision.polygons.some((p) => pointInPolygon(x, y, p.points));
}

/** Default sight radii, in fractions of the field. Tunable per token. */
export const SIGHT_RADIUS = {
  hero: 0.115,
  ward: 0.150,
  tower: 0.135,
  minion: 0.070,
};
